import { getApiErrorMessage } from '@moko/utils';
import { fromApiDateOnly, toApiDateOnly } from '../../utils/dateOnly';
import type {
  IPricingItem,
  ITenantPricingRate,
  ITenantPricingRateCreateBody,
  ITenantPricingRateUpdateBody,
} from '../../services/pricingService';
import { ANY_CONDITION, conditionId, conditionKey } from './pricingRateBody';

export const pricingItemLabel = (item: IPricingItem) =>
  item.name ? `${item.name} (${item.code})` : item.code;

export type TenantPricingOverrideFormValues = {
  pricingItemId: string;
  currencyCode: string;
  amount: string;
  effectiveFrom: string;
  effectiveTo: string;
  workTypeId: string;
  billingClassId: string;
  timeServiceId: string;
  isDiscountable: boolean;
};

export type TenantPricingOverrideStatus = 'Scheduled' | 'Active' | 'Expired';

export const emptyOverrideForm: TenantPricingOverrideFormValues = {
  pricingItemId: '',
  currencyCode: '',
  amount: '',
  effectiveFrom: '',
  effectiveTo: '',
  workTypeId: ANY_CONDITION,
  billingClassId: ANY_CONDITION,
  timeServiceId: ANY_CONDITION,
  isDiscountable: false,
};

const AMOUNT_PATTERN = /^\d+(\.\d{1,2})?$/;

export const validateOverrideAmount = (value: string): string | true => {
  const trimmed = value.trim();
  if (!trimmed) return 'Amount is required.';
  if (!AMOUNT_PATTERN.test(trimmed))
    return 'Amount must be a number with at most two decimal places.';
  if (Number(trimmed) <= 0) return 'Amount must be greater than 0.';
  return true;
};

export const validateOverrideEffectiveTo = (
  effectiveTo: string,
  effectiveFrom: string
): string | true => {
  const to = fromApiDateOnly(effectiveTo);
  const from = fromApiDateOnly(effectiveFrom);
  if (to && from && to < from)
    return 'Effective to must be on or after effective from.';
  return true;
};

// Inclusive date-only range; a missing end date is open-ended.
export const getOverrideStatus = (
  effectiveFrom: string,
  effectiveTo: string | null | undefined,
  today = toApiDateOnly(new Date())
): TenantPricingOverrideStatus => {
  const from = fromApiDateOnly(effectiveFrom);
  const to = fromApiDateOnly(effectiveTo);
  if (from && from > today) return 'Scheduled';
  if (to && to < today) return 'Expired';
  return 'Active';
};

export const toOverrideFormValues = (
  rate: ITenantPricingRate
): TenantPricingOverrideFormValues => ({
  pricingItemId: String(rate.pricingItemId),
  currencyCode: rate.currencyCode,
  amount: Number(rate.amount).toFixed(2),
  effectiveFrom: fromApiDateOnly(rate.effectiveFrom),
  effectiveTo: fromApiDateOnly(rate.effectiveTo),
  workTypeId: conditionKey(rate.workTypeId),
  billingClassId: conditionKey(rate.billingClassId),
  timeServiceId: conditionKey(rate.timeServiceId),
  isDiscountable: rate.isDiscountable === true,
});

export const toOverrideCreateBody = (
  values: TenantPricingOverrideFormValues
): ITenantPricingRateCreateBody => ({
  pricingItemId: Number(values.pricingItemId),
  currencyCode: values.currencyCode,
  amount: Number(values.amount.trim()),
  effectiveFrom: toApiDateOnly(values.effectiveFrom),
  effectiveTo: toApiDateOnly(values.effectiveTo) || null,
  workTypeId: conditionId(values.workTypeId),
  billingClassId: conditionId(values.billingClassId),
  timeServiceId: conditionId(values.timeServiceId),
  isDiscountable: values.isDiscountable,
});

// PUT keeps omitted fields, so only changed fields are sent; null clears effectiveTo.
export const toOverrideUpdateBody = (
  original: ITenantPricingRate,
  values: TenantPricingOverrideFormValues
): ITenantPricingRateUpdateBody => {
  const next = toOverrideCreateBody(values);
  const body: ITenantPricingRateUpdateBody = {};
  if (next.pricingItemId !== original.pricingItemId)
    body.pricingItemId = next.pricingItemId;
  if (next.currencyCode !== original.currencyCode)
    body.currencyCode = next.currencyCode;
  if (next.amount !== Number(original.amount)) body.amount = next.amount;
  if (next.effectiveFrom !== fromApiDateOnly(original.effectiveFrom))
    body.effectiveFrom = next.effectiveFrom;
  if (next.effectiveTo !== (fromApiDateOnly(original.effectiveTo) || null))
    body.effectiveTo = next.effectiveTo;
  if (next.workTypeId !== (original.workTypeId ?? null))
    body.workTypeId = next.workTypeId;
  if (next.billingClassId !== (original.billingClassId ?? null))
    body.billingClassId = next.billingClassId;
  if (next.timeServiceId !== (original.timeServiceId ?? null))
    body.timeServiceId = next.timeServiceId;
  if (next.isDiscountable !== (original.isDiscountable === true))
    body.isDiscountable = next.isDiscountable;
  return body;
};

const messagesFromErrors = (errors: unknown): string[] => {
  if (!errors || typeof errors !== 'object') return [];
  return Object.values(errors as Record<string, unknown>).flatMap((value) => {
    if (typeof value === 'string' && value.trim()) return [value.trim()];
    if (!Array.isArray(value)) return [];
    return value.filter(
      (item): item is string =>
        typeof item === 'string' && item.trim().length > 0
    );
  });
};

const API_STATUS = /API Error: (\d{3})/;

export const readApiProblem = (
  error: unknown
): { status: number; fieldErrors: string[] } => {
  const raw =
    error instanceof Error
      ? error.message
      : String((error as { message?: unknown })?.message ?? '');
  const status = Number(API_STATUS.exec(raw)?.[1]);
  const jsonStart = raw.indexOf('{');
  if (jsonStart < 0) return { status, fieldErrors: [] };
  try {
    return {
      status,
      fieldErrors: messagesFromErrors(
        (JSON.parse(raw.slice(jsonStart)) as { errors?: unknown }).errors
      ),
    };
  } catch {
    return { status, fieldErrors: [] };
  }
};

export const getOverrideErrorMessage = (
  error: unknown,
  fallback: string
): string => {
  const { status, fieldErrors } = readApiProblem(error);
  if (fieldErrors.length) return fieldErrors.join(' ');
  const detail = getApiErrorMessage(error, fallback);
  if (status === 409)
    return 'This override overlaps another override for the same pricing item and conditions. Effective dates are inclusive.';
  if (status === 404)
    return `Not found: ${detail}. The override, pricing item, or currency may no longer exist.`;
  if (status === 400) return `Invalid override: ${detail}`;
  return detail;
};
